import {
  AgentPurpose,
  AliveLine,
  type LastRun,
  LastRunLine,
  type RunNow,
  RunNowLine,
} from "@/components/agents/agent-work";
import { FlowDiagnosis } from "@/components/agents/flow-diagnosis";
import { MailPendingNotice } from "@/components/agents/mail-pending-notice";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LocalDate } from "@/components/ui/local-date";
import { AGENT_AVATAR_BY_SLUG } from "@/lib/agent-avatars";
import { formatTokens, formatUsd } from "@/lib/agents";
import { api } from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/agents` — admin-only. The AI workforce: one card per agent with its
 * portrait, what it is for, what it burns and the switch to pause it.
 *
 * The whole card opens the agent, the way a project card does — a link
 * on the name alone makes people aim at four words when the obvious
 * target is the card. The pause button is therefore a sibling of the
 * link rather than inside it: a button nested in an anchor is invalid
 * markup and would navigate on click.
 *
 * E2E behaviour contract lives in `e2e/agents.spec.ts`.
 */

type AgentCard = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "paused";
  lastRunAt: string | null;
  today: { tokens: number; costUsd: number };
  month: { tokens: number; costUsd: number };
  pendingSuggestions: number;
  billing: "metered" | "subscription";
  limitedUntil: string | null;
  accessError: string | null;
  accessMailPending: boolean;
  onFallback?: boolean;
  working: Array<{
    workItemId: string;
    globalId: number;
    title: string;
    projectName: string;
    userName: string | null;
    detail: string | null;
    startedAt: string;
  }>;
  /** Coder agents only, when no ticket is at hand: why. */
  idle: IdleReason[] | null;
  queue: { count: number; flagged: number };
  running: RunNow[];
  alive: Array<{ instance: string; at: string }> | null;
  lastFinished: LastRun | null;
};

type IdleReason =
  | { kind: "paused" | "draining" | "empty" }
  | { kind: "limited"; until: string }
  | { kind: "access_error"; error: string }
  | { kind: "ready" | "criteria" | "not_started"; count: number }
  | {
      kind: "waiting";
      count: number;
      unblocked: number;
      blockers: Array<{
        globalId: number;
        title: string;
        holderName: string | null;
        holderIsAgent: boolean;
        waiting: number;
      }>;
    };

type Blocker = {
  workItemId: string;
  globalId: number;
  title: string;
  projectName: string;
  status: string | null;
  holderName: string | null;
  blocks: number;
  since: string;
};

const tickets = (t: TFunction, n: number) => t("agents.tickets", { count: n });

/** Heartbeats age out without an event; a crashed agent never says it left. */
const STALE_RELOAD_MS = 60_000;

export const Route = createFileRoute("/_app/agents/")({
  component: AgentsPage,
});

function AgentsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [agents, setAgents] = useState<AgentCard[] | null>(null);
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user.globalRole === "admin";

  const load = useCallback(
    () =>
      api
        .get<{ agents: AgentCard[]; blockers: Blocker[] }>("/api/agents")
        .then((r) => {
          setAgents(r.agents);
          setBlockers(r.blockers ?? []);
        })
        .catch(() => setError(t("agents.loadFailed"))),
    [t],
  );

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    const socket = getSocket();
    let timer: ReturnType<typeof setTimeout> | null = null;
    // An agent starting or leaving a ticket is announced as a work item change.
    const onChange = (payload: { events?: Array<{ model?: string }> }) => {
      if (!payload?.events?.some((e) => e.model === "workItem")) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 500);
    };
    socket.on("change", onChange);
    const interval = setInterval(() => void load(), STALE_RELOAD_MS);
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(interval);
      socket.off("change", onChange);
    };
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/" />;

  async function setStatus(slug: string, status: "active" | "paused") {
    setAgents((prev) => prev?.map((a) => (a.slug === slug ? { ...a, status } : a)) ?? null);
    await api.patch(`/api/agents/${encodeURIComponent(slug)}`, { status }).catch(() => void load());
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="font-bold text-3xl">{t("agents.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("agents.subtitle")}</p>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {blockers.length > 0 && <BlockersList blockers={blockers} />}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {agents?.map((a) => (
          <AgentTile
            key={a.slug}
            agent={a}
            onToggle={() => setStatus(a.slug, a.status === "active" ? "paused" : "active")}
          />
        ))}
      </div>

      {agents && agents.length === 0 && (
        <p className="text-muted-foreground text-sm">{t("agents.none")}</p>
      )}

      <LairAgents />

      <LairRunners />

      <FlowDiagnosis />
    </div>
  );
}

function BlockersList({ blockers }: { blockers: Blocker[] }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border bg-card p-5" data-testid="agents-blockers">
      <h2 className="font-bold font-heading text-xl">{t("agents.blockers.title")}</h2>
      <p className="mt-1 text-muted-foreground text-sm">{t("agents.blockers.intro")}</p>
      <ul className="mt-3 divide-y">
        {blockers.map((b) => (
          <li key={b.workItemId} className="py-2" data-testid="agents-blocker">
            <Link
              to="/work-item/$id"
              params={{ id: String(b.globalId) }}
              className="text-sm hover:underline"
            >
              <span className="font-medium">#{b.globalId}</span> {b.title}
            </Link>
            <Badge variant="warning" className="ml-2" data-testid="agents-blocker-count">
              {t("agents.blockers.blocks", { count: b.blocks })}
            </Badge>
            <span className="block text-muted-foreground text-xs">
              {b.projectName} ·{" "}
              {b.holderName
                ? t("agents.blockers.with", { name: b.holderName })
                : t("agents.blockers.nobody")}
              {b.status ? ` · ${b.status}` : ""} · {t("agents.blockers.since")}{" "}
              <LocalDate value={b.since} withTime />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function IdleLine({ reason: r }: { reason: IdleReason }) {
  const { t } = useTranslation();
  switch (r.kind) {
    case "paused":
      return <>{t("agents.idle.paused")}</>;
    case "draining":
      return <>{t("agents.idle.draining")}</>;
    case "limited":
      return (
        <>
          {t("agents.idle.limited")} <LocalDate value={r.until} withTime />.
        </>
      );
    case "access_error":
      return <>{t("agents.idle.accessError", { error: r.error })}</>;
    case "ready":
      return <>{t("agents.idle.ready", { tickets: tickets(t, r.count) })}</>;
    case "criteria":
      return <>{t("agents.idle.criteria", { tickets: tickets(t, r.count) })}</>;
    case "not_started":
      return <>{t("agents.idle.notStarted", { tickets: tickets(t, r.count) })}</>;
    case "waiting":
      return (
        <>
          {t("agents.idle.waiting", { tickets: tickets(t, r.count) })}
          {r.blockers.length > 0 ? ":" : "."}
          {r.blockers.map((b) => (
            <Link
              key={b.globalId}
              to="/work-item/$id"
              params={{ id: String(b.globalId) }}
              className="block pl-3 hover:underline"
              data-testid="agent-idle-blocker"
            >
              <span className="font-medium">#{b.globalId}</span> {b.title} ·{" "}
              {t("agents.idle.holds", { count: b.waiting })} ·{" "}
              {b.holderName
                ? t("agents.blockers.with", { name: b.holderName })
                : t("agents.blockers.nobody")}
            </Link>
          ))}
          {r.unblocked > 0 && (
            <span className="block pl-3">
              {t("agents.idle.unblocked", { tickets: tickets(t, r.unblocked) })}
            </span>
          )}
        </>
      );
    case "empty":
      return <>{t("agents.idle.empty")}</>;
  }
}

function AgentTile({ agent: a, onToggle }: { agent: AgentCard; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    // `h-full` on both: the grid stretches the wrapper, and without it
    // on the link the card's border would stop at its own content and
    // the row would look ragged.
    <div
      className="relative flex h-full flex-col overflow-hidden rounded-lg border bg-card"
      data-testid="agent-card"
    >
      <Link
        to="/agents/$slug"
        params={{ slug: a.slug }}
        className="block flex-1 p-5 transition-colors hover:bg-accent/30"
        data-testid="agent-open"
      >
        <div className="flex items-start gap-4">
          <Avatar src={AGENT_AVATAR_BY_SLUG[a.slug]} name={a.name} size={88} />
          <div className="min-w-0 flex-1">
            {/* Right padding leaves the pause button its corner. */}
            <h2 className="pr-20 font-bold font-heading text-xl leading-snug">{a.name}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Badge
                variant={a.status === "active" ? "default" : "secondary"}
                data-testid="agent-status"
              >
                {a.status === "active" ? t("agents.active") : t("agents.paused")}
              </Badge>
              {a.limitedUntil && (
                <Badge variant="secondary" data-testid="agent-limited">
                  {t("agents.tile.waitingForLimit")}
                </Badge>
              )}
              {a.onFallback && (
                <Badge variant="secondary" data-testid="agent-on-fallback">
                  {t("agents.tile.onFallback")}
                </Badge>
              )}
              {a.accessError && (
                <Badge variant="destructive" data-testid="agent-access-error">
                  {t("agents.tile.tokenRefused")}
                </Badge>
              )}
              {a.pendingSuggestions > 0 && (
                <Badge variant="warning" data-testid="agent-pending">
                  {t("agents.tile.pending", { count: a.pendingSuggestions })}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <MailPendingNotice pending={a.accessMailPending} />

        {a.description && (
          <p className="mt-3 line-clamp-3 text-muted-foreground text-sm">{a.description}</p>
        )}
        <AgentPurpose slug={a.slug} />

        <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-today">
              {formatTokens(a.today.tokens)} {t("agents.tile.tok")} · {formatUsd(a.today.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">{t("agents.tile.today")}</div>
          </div>
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-month">
              {formatTokens(a.month.tokens)} {t("agents.tile.tok")} · {formatUsd(a.month.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">
              {t("agents.tile.thisMonth")}
              {a.billing === "subscription" && (
                <span data-testid="agent-billing"> · {t("agents.tile.estimateSubscription")}</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 text-muted-foreground text-xs">
          {t("agents.tile.lastActivity")}{" "}
          {a.lastRunAt ? <LocalDate value={a.lastRunAt} withTime /> : t("agents.never")}
        </div>
      </Link>

      {/* Outside the card's link: a ticket link nested in it would be invalid markup. */}
      {a.working.length > 0 && (
        <div className="space-y-1.5 border-t px-5 py-3" data-testid="agent-working">
          <div className="text-muted-foreground text-xs">{t("agents.tile.workingOn")}</div>
          {a.working.map((w) => (
            <Link
              key={w.workItemId}
              to="/work-item/$id"
              params={{ id: String(w.globalId) }}
              className="block rounded-sm text-sm hover:underline"
              data-testid="agent-working-ticket"
            >
              <span className="font-medium">#{w.globalId}</span> {w.title}
              <span className="block text-muted-foreground text-xs">
                {w.projectName}
                {w.userName ? ` · ${t("agents.tile.withUser", { name: w.userName })}` : ""}
                {w.detail ? ` · ${w.detail}` : ""} · {t("agents.blockers.since")}{" "}
                <LocalDate value={w.startedAt} withTime />
              </span>
            </Link>
          ))}
        </div>
      )}

      {a.idle && a.idle.length > 0 && (
        <div className="space-y-1 border-t px-5 py-3 text-sm" data-testid="agent-idle">
          <div className="text-muted-foreground text-xs">{t("agents.idle.title")}</div>
          {a.idle.map((r) => (
            <div key={r.kind} data-testid={`agent-idle-${r.kind}`}>
              <IdleLine reason={r} />
            </div>
          ))}
        </div>
      )}

      <AgentNowAndQueue agent={a} />

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="absolute top-4 right-4"
        onClick={onToggle}
        data-testid="agent-toggle"
      >
        {a.status === "active" ? t("agents.pause") : t("agents.resume")}
      </Button>
    </div>
  );
}

/** Runs the presence block above doesn't already show (a mailbox pass, a review), the queue size and the last run. */
function AgentNowAndQueue({ agent: a }: { agent: AgentCard }) {
  const { t } = useTranslation();
  const shown = new Set(a.working.map((w) => w.workItemId));
  const running = a.running.filter((r) => !r.workItem || !shown.has(r.workItem.id));
  return (
    <div className="space-y-1.5 border-t px-5 py-3" data-testid="agent-now-queue">
      <AliveLine alive={a.alive} />
      {running.length > 0 && (
        <>
          <div className="text-muted-foreground text-xs">{t("agentWork.now.title")}</div>
          {running.map((r) => (
            <RunNowLine key={r.id} run={r} />
          ))}
        </>
      )}
      <Link
        to="/agents/$slug/activity"
        params={{ slug: a.slug }}
        className="block text-sm hover:underline"
        data-testid="agent-queue-link"
      >
        <span className="font-medium">{t("agentWork.queue.title")}:</span>{" "}
        {t("agentWork.queue.count", { count: a.queue.count })}
        {a.queue.flagged > 0 && (
          <Badge variant="warning" className="ml-2" data-testid="agent-queue-flagged">
            {t("agentWork.queue.flagged", { count: a.queue.flagged })}
          </Badge>
        )}
      </Link>
      <LastRunLine run={a.lastFinished} />
    </div>
  );
}

type LairRunner = {
  id: string;
  machine: string;
  createdAt: string;
  contactedAt: string | null;
  user: { id: string; name: string };
};

/** Machines whose lair takes Hub's lair-local CI jobs; the switch is each person's, in lair. */
function LairRunners() {
  const { t } = useTranslation();
  const [runners, setRunners] = useState<LairRunner[] | null>(null);
  useEffect(() => {
    void api
      .get<{ runners: LairRunner[] }>("/api/agents/lair/runners")
      .then((r) => setRunners(r.runners))
      .catch(() => setRunners([]));
  }, []);
  if (!runners?.length) return null;
  return (
    <section className="space-y-3" data-testid="lair-runners">
      <div>
        <h2 className="font-bold font-heading text-xl">{t("agents.lair.runners.title")}</h2>
        <p className="mt-1 text-muted-foreground text-sm">{t("agents.lair.runners.subtitle")}</p>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-4 font-medium">{t("agents.lair.runners.person")}</th>
            <th className="py-1 pr-4 font-medium">{t("agents.lair.runners.machine")}</th>
            <th className="py-1 pr-4 font-medium">{t("agents.lair.runners.created")}</th>
            <th className="py-1 pr-4 font-medium">{t("agents.lair.runners.contacted")}</th>
          </tr>
        </thead>
        <tbody>
          {runners.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="py-1 pr-4">{r.user.name}</td>
              <td className="py-1 pr-4 font-mono">{r.machine}</td>
              <td className="py-1 pr-4">
                <LocalDate value={r.createdAt} withTime />
              </td>
              <td className="py-1 pr-4">
                {r.contactedAt ? (
                  <LocalDate value={r.contactedAt} withTime />
                ) : (
                  t("agents.lair.runners.never")
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

type LairPersona = { persona: string; on: number; total: number };

/** lair's agents run on the person's own machine and subscription: no queue, no spend here, just their skills. */
function LairAgents() {
  const { t } = useTranslation();
  const [personas, setPersonas] = useState<LairPersona[] | null>(null);
  useEffect(() => {
    void api
      .get<{ personas: LairPersona[] }>("/api/agents/lair")
      .then((r) => setPersonas(r.personas))
      .catch(() => setPersonas([]));
  }, []);
  if (!personas?.length) return null;
  return (
    <section className="space-y-3" data-testid="agents-lair">
      <div>
        <h2 className="font-bold font-heading text-xl">{t("agents.lair.title")}</h2>
        <p className="mt-1 text-muted-foreground text-sm">{t("agents.lair.subtitle")}</p>
        <p className="mt-2 text-muted-foreground text-sm" data-testid="agents-lair-claims">
          {t("agents.lair.claims")}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {personas.map((p) => (
          <Link
            key={p.persona}
            to="/agents/lair/$persona"
            params={{ persona: p.persona }}
            className="block rounded-lg border bg-card p-4 transition-colors hover:border-primary/50"
            data-testid="lair-agent-card"
          >
            <div className="font-bold font-heading text-lg">
              {t(`agents.lair.personas.${p.persona}.name`)}
            </div>
            <p className="mt-1 line-clamp-2 text-muted-foreground text-sm">
              {t(`agents.lair.personas.${p.persona}.description`)}
            </p>
            <p className="mt-3 border-t pt-2 text-xs" data-testid="lair-agent-skills">
              {t("agents.lair.skillCount", { on: p.on, total: p.total })}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}
